
import { defineComponent, ref, computed, watch, onMounted } from "vue";
import { object, string } from "yup";
import ApiService from "@/core/services/ApiService";
import { apiEndpoint } from "@/mixin/apiMixin";
import { VueCookieNext } from "vue-cookie-next";
import { ElNotification } from "element-plus";
import { useBus } from "@/bus";
import { DrawerComponent } from "@/assets/ts/components";

type GstPlanItem = {
  gst_activity: string;
  required_indicator: string;
};

export default defineComponent({
  mixins: [apiEndpoint],
  name: "GenderPolicyModal",
  props: {
    data: { type: Object },
  },
  setup(props) {
    const { bus } = useBus();
    const ministryOptions = ref([]);
    const gstPlanItems = ref<GstPlanItem[]>([]);
    const governmentPriorityOptions = ref<string[]>([]);
    let editingMinistry = "";
    const formData = ref({
      id: "",
      updated_by: "",
      created_by: VueCookieNext.getCookie("_user_id"),
      ministry: "",
      gst_activity: "",
      required_indicator: "",
      baseline: "",
      annual_target: "",
      achievement_reporting_period: "",
      cumulative_achievement: "",
      reporting_frequency: "",
      evidence_source: "",
      challenges: "",
      government_priority: [] as string[],
      election_manifesto_target_aligned: "",
      remarks: "",
      budget_2026_27: "",
      expenditure_2026_27: "",
      submit_date: "",
    });

    watch(
      () => props.data,
      (newData) => {
        if (newData) {
          editingMinistry = newData.ministry || "";

          formData.value = {
            ...formData.value,
            ...newData,
          };

          const gp =
            newData.government_priority ||
            newData.election_manifesto_target_aligned ||
            "";
          formData.value.government_priority = gp.split(", ").filter(Boolean);
          formData.value.election_manifesto_target_aligned = "";

          loadGstPlanItems(editingMinistry);
          loadGovernmentPriorities(editingMinistry);
        }
      }
    );

    watch(
      () => formData.value.ministry,
      (val) => {
        if (val === editingMinistry) {
          return;
        }

        formData.value.gst_activity = "";
        formData.value.required_indicator = "";
        formData.value.government_priority = [];
        loadGstPlanItems(val);
        loadGovernmentPriorities(val);
      }
    );

    watch(
      () => formData.value.gst_activity,
      (val) => {
        if (!val) {
          return;
        }

        const item = gstPlanItems.value.find((i) => i.gst_activity === val);
        if (item) {
          formData.value.required_indicator = item.required_indicator;
        }
      }
    );

    const gstActivityOptions = computed(() => {
      return [...new Set(gstPlanItems.value.map((i) => i.gst_activity))];
    });

    const loadGstPlanItems = async (ministry) => {
      if (!ministry) {
        gstPlanItems.value = [];
        return;
      }

      try {
        const response = await ApiService.post(
          apiEndpoint.data().VUE_APP_GST_ACTION_PLAN_LIST,
          {
            ministry: ministry,
          }
        );

        gstPlanItems.value = response.data.data;
      } catch (error) {
        console.error("Error fetching gst action plan items:", error);
      }
    };

    const loadGovernmentPriorities = async (ministry) => {
      if (!ministry) {
        governmentPriorityOptions.value = [];
        return;
      }

      try {
        const response = await ApiService.post(
          apiEndpoint.data().VUE_APP_GOVERNMENT_PRIORITY_LIST,
          {
            ministry: ministry,
          }
        );

        governmentPriorityOptions.value = response.data.data.map(
          (item) => item.name
        );
      } catch (error) {
        console.error("Error fetching government priorities:", error);
      }
    };

    const schema = object().shape({
      ministry: string().required("Ministry is required"),
      gst_activity: string().required("GST Activity is required"),
      required_indicator: string().required("Required Indicator is required"),
    });

    const errors = ref({});

    const getMinistryOptions = async () => {
      try {
        const response = await ApiService.post(
          apiEndpoint.data().VUE_APP_MINISTRY_LIST,
          {}
        );

        ministryOptions.value = response.data.data.map((option) => option.name);
      } catch (error) {
        console.error("Error fetching ministry options:", error);
      }
    };

    const save = async () => {
      try {
        if (formData.value.id) {
          formData.value.updated_by = VueCookieNext.getCookie("_user_id");
        }

        await schema.validate(formData.value, { abortEarly: false });

        const payload = {
          ...formData.value,
          government_priority: (formData.value.government_priority || []).join(
            ", "
          ),
        };

        ApiService.post(
          apiEndpoint.data().VUE_APP_GENDER_POLICY_REPORT_STORE,
          payload
        )
          .then((response) => {
            if (response.data.status == "success") {
              ElNotification({
                dangerouslyUseHTMLString: true,
                message: response.data.data,
                type: "success",
              });
              DrawerComponent.hideAll();
              bus.emit("loadData", true);
            } else {
              ElNotification({
                dangerouslyUseHTMLString: true,
                message: response.data.msg,
                type: "error",
              });
            }
          })
          .catch(({ response }) => {
            console.log(response);
          });

        formData.value = {
          id: "",
          updated_by: "",
          created_by: VueCookieNext.getCookie("_user_id"),
          ministry: "",
          gst_activity: "",
          required_indicator: "",
          baseline: "",
          annual_target: "",
          achievement_reporting_period: "",
          cumulative_achievement: "",
          reporting_frequency: "",
          evidence_source: "",
          challenges: "",
          government_priority: [] as string[],
          election_manifesto_target_aligned: "",
          remarks: "",
          budget_2026_27: "",
          expenditure_2026_27: "",
          submit_date: "",
        };
        gstPlanItems.value = [];
        governmentPriorityOptions.value = [];
        errors.value = {};
      } catch (validationErrors) {
        errors.value = validationErrors.inner.reduce((acc, error) => {
          acc[error.path] = error.message;
          return acc;
        }, {});
      }
    };

    onMounted(() => {
      DrawerComponent.bootstrap();
      DrawerComponent.updateAll();

      getMinistryOptions();
    });

    return {
      formData,
      errors,
      save,
      ministryOptions,
      gstActivityOptions,
      governmentPriorityOptions,
    };
  },
});
